import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, ArrowRight } from "lucide-react";
import { Reveal, MaskedLines } from "@/components/Reveal";

const strip = { background: "linear-gradient(90deg, #8c6d3f 0%, #e5c158 50%, #8c6d3f 100%)" };
const goldBg = { background: "linear-gradient(135deg, #a87f24 0%, #e5c158 50%, #d4af37 100%)" };

const CATEGORIES = [
  {
    numeral: "I",
    name: "General Funding",
    items: [
      {
        q: "What does Emison Management do?",
        a: "We are your funding partner — a consultancy that secures loans, lines of credit, and business credit cards for artists and record labels. We package your profile, position you with our lender network, and negotiate the limits, terms, and speed you're looking for.",
      },
      {
        q: "Do I give up ownership of my music?",
        a: "Never. Every product we arrange is debt, not equity. You retain 100% of your masters, publishing, and rights — that's the Vault Guarantee.",
      },
      {
        q: "How much funding can I access?",
        a: "Funding ranges from $25,000 to $10M+, depending on your streaming performance, royalty income, credit profile, and the structure we build together.",
      },
      {
        q: "How fast is the process?",
        a: "We've refined our process so you go from interested to funded with clarity at every step. Assessments come back within 24 hours, and offers on new projects typically land in a week or less.",
      },
    ],
  },
  {
    numeral: "II",
    name: "Products & Structure",
    items: [
      {
        q: "What's the difference between an artist loan and a line of credit?",
        a: "A loan gives you a lump sum for a defined purpose — a tour, an album, a marketing push. A line of credit is flexible capital you draw from as needed, ideal for labels funding signings and releases on a rolling basis.",
      },
      {
        q: "Why business credit cards?",
        a: "Strategically positioned business credit cards build your company's credit profile while covering day-to-day spend. We hand-select cards from our curated lender network — without wasting inquiries.",
      },
      {
        q: "What is a catalog advance?",
        a: "An advance against the future earnings of your masters or publishing. Unlike a traditional label advance, our structures let you retain ownership and customize the deal length and royalty rate applied.",
      },
      {
        q: "Can labels fund new signings through you?",
        a: "Yes. Labels use our lines of credit and advances to sign new artists, finance releases, expand operations, and even buy out of bad deals to leverage better terms elsewhere.",
      },
    ],
  },
  {
    numeral: "III",
    name: "Qualifications & Repayment",
    items: [
      {
        q: "What do I need to qualify?",
        a: "We look at three pillars — your streaming and revenue performance, your credit and cash flow integrity, and your growth strategy. A consistent royalty history strengthens every application.",
      },
      {
        q: "Will applying hurt my credit?",
        a: "Submitting the vault application is a soft review — no hard inquiry. We map your options first, and only proceed with your approval.",
      },
      {
        q: "How does repayment work?",
        a: "Terms are tailored to your cash flow — monthly payments, royalty-linked schedules, or revolving credit. We negotiate structure around your income cycles, not against them.",
      },
      {
        q: "What if my project underperforms?",
        a: "We structure conservatively and transparently from day one, and we walk you through every scenario before you sign. Our goal is funding you can grow with — not debt that buries you.",
      },
    ],
  },
];

const Item = ({ q, a, testid }) => {
  const [open, setOpen] = useState(false);
  return (
    <div
      className={`group relative bg-[#fffdf9] border transition-all duration-500 ${
        open ? "border-[#c9a227] shadow-[0_25px_60px_rgba(201,162,39,0.22)]" : "border-[#e3d3ac] hover:border-[#c9a227]/70 shadow-[0_12px_35px_rgba(45,26,11,0.1)]"
      }`}
    >
      <span className="absolute bottom-0 left-0 right-0 h-[3px]" style={strip} />
      <button
        data-testid={testid}
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-6 p-6 sm:p-8 text-left"
      >
        <span className={`font-serif text-lg sm:text-xl transition-colors duration-300 ${open ? "text-[#4D4232]" : "text-[#4D4232]"}`}>{q}</span>
        <motion.span
          animate={{ rotate: open ? 135 : 0 }}
          transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
          className="w-9 h-9 rotate-45 flex items-center justify-center shrink-0 border border-[#c9a227]/60"
          style={open ? goldBg : {}}
        >
          <Plus className={`-rotate-45 w-4 h-4 ${open ? "text-white" : "text-gold"}`} />
        </motion.span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
          >
            <div className="px-6 sm:px-8 pb-8">
              <span className="block h-px w-16 bg-gradient-to-r from-[#c9a227] to-transparent mb-5" />
              <p className="text-sm sm:text-base text-sand leading-relaxed max-w-3xl">{a}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const Faq = () => {
  let idx = 0;
  return (
    <main data-testid="faq-page" className="pt-20">
      {/* HERO */}
      <section className="relative overflow-hidden py-24 lg:py-36 px-6 lg:px-12">
        <span
          aria-hidden="true"
          className="pointer-events-none select-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 font-serif italic whitespace-nowrap text-[26vw] leading-none text-transparent"
          style={{ WebkitTextStroke: "1px rgba(212,175,55,0.12)" }}
        >
          FAQ
        </span>
        <div className="relative max-w-4xl mx-auto text-center">
          <div className="flex items-center justify-center gap-4 mb-8">
            <span className="h-px w-12 sm:w-20 bg-gradient-to-r from-transparent to-[#d4af37]" />
            <span className="w-2 h-2 rotate-45 bg-[#d4af37]" />
            <p className="text-[10px] font-mono-accent uppercase tracking-[0.4em] text-gold">
              Frequently Asked Questions
            </p>
            <span className="w-2 h-2 rotate-45 bg-[#d4af37]" />
            <span className="h-px w-12 sm:w-20 bg-gradient-to-l from-transparent to-[#d4af37]" />
          </div>
          <h1 className="font-serif font-semibold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.08] text-[#f3e5c8]">
            <MaskedLines
              lines={[<>Everything you'd want to know</>, <span className="italic gold-text">before the vault opens.</span>]}
            />
          </h1>
          <p className="mt-8 text-base text-[#cbb493] leading-relaxed max-w-xl mx-auto">
            Straight answers on funding, ownership, speed, and structure — the
            way we'd want them ourselves.
          </p>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="px-6 lg:px-12 pb-24 max-w-4xl mx-auto space-y-20">
        {CATEGORIES.map((cat) => (
          <div key={cat.name}>
            <Reveal>
              <div className="flex items-center gap-5 mb-10">
                <span
                  className="w-12 h-12 rotate-45 flex items-center justify-center shrink-0 shadow-[0_8px_20px_rgba(201,162,39,0.4)]"
                  style={goldBg}
                >
                  <span className="-rotate-45 font-serif italic text-white text-base">{cat.numeral}</span>
                </span>
                <p className="text-[11px] font-mono-accent uppercase tracking-[0.35em] text-gold">
                  {cat.name}
                </p>
                <span className="flex-1 h-px bg-gradient-to-r from-[#c9a227]/60 to-transparent" />
              </div>
            </Reveal>
            <div className="space-y-4">
              {cat.items.map((item) => {
                idx += 1;
                return (
                  <Reveal key={item.q} delay={0.04 * idx}>
                    <Item q={item.q} a={item.a} testid={`faq-accordion-item-${idx}`} />
                  </Reveal>
                );
              })}
            </div>
          </div>
        ))}
      </section>

      {/* CTA */}
      <section className="px-6 lg:px-12 pb-32 max-w-4xl mx-auto">
        <Reveal>
          <div className="relative bg-[#fffdf9] border border-[#e3d3ac] p-10 lg:p-14 shadow-[0_30px_80px_rgba(45,26,11,0.22)] flex flex-col md:flex-row md:items-center justify-between gap-8">
            <span className="absolute bottom-0 left-0 right-0 h-[3px]" style={strip} />
            <div>
              <span
                className="w-10 h-10 rotate-45 flex items-center justify-center mb-6 shadow-[0_8px_20px_rgba(201,162,39,0.4)]"
                style={goldBg}
              >
                <span className="-rotate-45 font-serif italic text-white text-sm">E</span>
              </span>
              <h2 className="font-serif text-2xl sm:text-3xl text-[#4D4232]">Still have a question?</h2>
              <p className="mt-3 text-sm text-sand leading-relaxed max-w-md">
                Talk to a real funding consultant — or begin your application and
                get a real answer within 24 hours.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 shrink-0">
              <a
                href="mailto:info@emisonmanagement.com"
                data-testid="faq-contact-email"
                className="btn-outline-lux text-champagne text-[11px] font-semibold font-mono-accent uppercase tracking-[0.25em] px-8 py-4 hover:text-gold text-center"
              >
                Email Us
              </a>
              <Link
                to="/apply"
                data-testid="faq-cta-apply"
                className="sheen-btn text-[#4D4232] text-[11px] font-semibold font-mono-accent uppercase tracking-[0.25em] px-8 py-4 shadow-[0_10px_25px_rgba(201,162,39,0.4)] hover:scale-[1.03] transition-transform flex items-center justify-center gap-3"
                style={goldBg}
              >
                Apply Now <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </Reveal>
      </section>
    </main>
  );
};

export default Faq;
